// CB:BEGIN admin-priority2-financial-reports-viewer-component
/**
 * Financial Reports Viewer Component
 * Displays revenue reports, contractor earnings, P&L, and transaction exports
 */
import React, { useState } from 'react';
import { useApi } from '../../lib/useApi';

type ReportType = 'revenue' | 'earnings' | 'p-and-l' | 'transactions';

export const FinancialReportsViewer: React.FC = () => {
  const api = useApi();
  const [reportType, setReportType] = useState<ReportType>('revenue');
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportData, setReportData] = useState<any>(null);

  const handleGenerateReport = async () => {
    setLoading(true);
    setReportData(null);

    try {
      let endpoint = '';
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      switch (reportType) {
        case 'revenue':
          endpoint = `/api/admin/reports/revenue?${params.toString()}`;
          break;
        case 'earnings':
          endpoint = `/api/admin/reports/contractor-earnings?${params.toString()}`;
          break;
        case 'p-and-l':
          endpoint = `/api/admin/reports/p-and-l?${params.toString()}`;
          break;
        case 'transactions':
          endpoint = `/api/admin/reports/transactions?${params.toString()}`;
          break;
      }

      const data = await api.get(endpoint);
      setReportData(data);
    } catch (e: any) {
      alert(`❌ Error: ${e.message || 'Failed to generate report'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    if (reportType !== 'transactions') {
      alert('CSV export only available for transactions report');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      params.append('format', 'csv');

      const response = await fetch(`/api/admin/reports/transactions?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) throw new Error('Export failed');

      const csv = await response.text();
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      alert('✅ CSV export downloaded');
    } catch (e: any) {
      alert(`❌ Error: ${e.message || 'Export failed'}`);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 24px 0', fontSize: '24px', fontWeight: '700' }}>
        Financial Reports
      </h2>

      {/* Report Configuration */}
      <div style={{
        padding: '20px',
        background: '#f9fafb',
        borderRadius: '8px',
        marginBottom: '24px',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Report Type Selection */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
              Report Type
            </label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {[
                { value: 'revenue', label: 'Platform Revenue' },
                { value: 'earnings', label: 'Contractor Earnings' },
                { value: 'p-and-l', label: 'Profit & Loss' },
                { value: 'transactions', label: 'Transactions' }
              ].map(type => (
                <button
                  key={type.value}
                  onClick={() => setReportType(type.value as ReportType)}
                  style={{
                    padding: '8px 16px',
                    background: reportType === type.value ? '#3b82f6' : '#fff',
                    color: reportType === type.value ? '#fff' : '#374151',
                    border: '1px solid',
                    borderColor: reportType === type.value ? '#3b82f6' : '#d1d5db',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Date Range Filters */}
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                Start Date (Optional)
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                End Date (Optional)
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleGenerateReport}
              disabled={loading}
              style={{
                padding: '10px 20px',
                background: loading ? '#9ca3af' : '#10b981',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </button>

            {reportType === 'transactions' && reportData && (
              <button
                onClick={handleExportCSV}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  background: '#6b7280',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Export CSV
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Report Results */}
      {reportData && (
        <div style={{
          padding: '20px',
          background: '#fff',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          {reportType === 'revenue' && (
            <div>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: '600' }}>
                Revenue Report
              </h3>

              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                  <div style={{ padding: '16px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac' }}>
                    <div style={{ fontSize: '14px', color: '#166534', marginBottom: '4px' }}>Total Revenue</div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#15803d' }}>
                      {formatCurrency(reportData.totalRevenueCents)}
                    </div>
                  </div>
                  <div style={{ padding: '16px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #93c5fd' }}>
                    <div style={{ fontSize: '14px', color: '#1e40af', marginBottom: '4px' }}>Transactions</div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#1e3a8a' }}>
                      {reportData.transactionCount}
                    </div>
                  </div>
                </div>
              </div>

              {reportData.byMonth && reportData.byMonth.length > 0 && (
                <div>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>
                    Revenue by Month
                  </h4>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Month</th>
                          <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: '600' }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.byMonth.map((month: any) => (
                          <tr key={month.month} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '12px', fontSize: '14px' }}>{month.month}</td>
                            <td style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: '600' }}>
                              {formatCurrency(month.amountCents)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {reportType === 'earnings' && (
            <div>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: '600' }}>
                Contractor Earnings Report
              </h3>

              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                  <div style={{ padding: '16px', background: '#fef3c7', borderRadius: '8px', border: '1px solid #fbbf24' }}>
                    <div style={{ fontSize: '14px', color: '#92400e', marginBottom: '4px' }}>Total Paid</div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#78350f' }}>
                      {formatCurrency(reportData.totalPaidCents)}
                    </div>
                  </div>
                  <div style={{ padding: '16px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #93c5fd' }}>
                    <div style={{ fontSize: '14px', color: '#1e40af', marginBottom: '4px' }}>Contractors</div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#1e3a8a' }}>
                      {reportData.totalContractors}
                    </div>
                  </div>
                </div>
              </div>

              {reportData.contractorEarnings && reportData.contractorEarnings.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Company</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Email</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: '600' }}>Transactions</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: '600' }}>Total Earnings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.contractorEarnings.slice(0, 50).map((contractor: any) => (
                        <tr key={contractor.contractorId} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '12px', fontSize: '14px', fontWeight: '500' }}>{contractor.company}</td>
                          <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>{contractor.email}</td>
                          <td style={{ padding: '12px', textAlign: 'right', fontSize: '14px' }}>{contractor.transactionCount}</td>
                          <td style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: '600' }}>
                            {formatCurrency(contractor.totalEarningsCents)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {reportType === 'p-and-l' && (
            <div>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: '600' }}>
                Profit & Loss Statement
              </h3>

              <div style={{ marginBottom: '24px' }}>
                <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '8px', marginBottom: '12px' }}>
                  <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Period</div>
                  <div style={{ fontSize: '16px', fontWeight: '600' }}>
                    {reportData.period.startDate} to {reportData.period.endDate}
                  </div>
                </div>

                <div style={{ display: 'grid', gap: '16px' }}>
                  <div style={{ padding: '16px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac' }}>
                    <div style={{ fontSize: '14px', color: '#166534', marginBottom: '8px' }}>Gross Revenue</div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#15803d' }}>
                      {formatCurrency(reportData.revenue.grossRevenueCents)}
                    </div>
                  </div>

                  <div style={{ padding: '16px', background: '#fee2e2', borderRadius: '8px', border: '1px solid #fca5a5' }}>
                    <div style={{ fontSize: '14px', color: '#991b1b', marginBottom: '8px' }}>Refunds</div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#7f1d1d' }}>
                      -{formatCurrency(reportData.revenue.refundsCents)}
                    </div>
                  </div>

                  <div style={{ padding: '16px', background: '#dbeafe', borderRadius: '8px', border: '2px solid #3b82f6' }}>
                    <div style={{ fontSize: '14px', color: '#1e40af', marginBottom: '8px', fontWeight: '600' }}>Net Revenue</div>
                    <div style={{ fontSize: '32px', fontWeight: '700', color: '#1e3a8a' }}>
                      {formatCurrency(reportData.revenue.netRevenueCents)}
                    </div>
                  </div>
                </div>
              </div>

              {reportData.transactionSummary && (
                <div>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>
                    Transaction Summary
                  </h4>
                  <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '8px' }}>
                    <div style={{ marginBottom: '8px', fontSize: '14px' }}>
                      <strong>Total Transactions:</strong> {reportData.transactionSummary.totalTransactions}
                    </div>
                    {reportData.transactionSummary.byType.map((type: any) => (
                      <div key={type.type} style={{ fontSize: '14px', color: '#6b7280', marginLeft: '16px' }}>
                        {type.type}: {type.count}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {reportType === 'transactions' && (
            <div>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: '600' }}>
                Transactions Report
              </h3>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                  <div style={{ padding: '16px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #93c5fd' }}>
                    <div style={{ fontSize: '14px', color: '#1e40af', marginBottom: '4px' }}>Total Transactions</div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#1e3a8a' }}>
                      {reportData.count}
                    </div>
                  </div>
                  <div style={{ padding: '16px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac' }}>
                    <div style={{ fontSize: '14px', color: '#166534', marginBottom: '4px' }}>Total Amount</div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#15803d' }}>
                      {formatCurrency(reportData.totalAmountCents)}
                    </div>
                  </div>
                </div>
              </div>

              {reportData.transactions && reportData.transactions.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600' }}>ID</th>
                        <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600' }}>Type</th>
                        <th style={{ padding: '10px', textAlign: 'right', fontWeight: '600' }}>Amount</th>
                        <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600' }}>Project</th>
                        <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600' }}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.transactions.slice(0, 100).map((tx: any) => (
                        <tr key={tx.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '10px', fontFamily: 'monospace' }}>{tx.id.substring(0, 8)}</td>
                          <td style={{ padding: '10px' }}>
                            <span style={{
                              padding: '2px 8px',
                              background: tx.type === 'FEE' ? '#fef3c7' : '#dbeafe',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}>
                              {tx.type}
                            </span>
                          </td>
                          <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600' }}>
                            {formatCurrency(tx.amountCents)}
                          </td>
                          <td style={{ padding: '10px', color: '#6b7280', fontSize: '12px' }}>
                            {tx.escrow?.project?.address || 'N/A'}
                          </td>
                          <td style={{ padding: '10px', color: '#6b7280' }}>
                            {new Date(tx.createdAt).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!reportData && !loading && (
        <div style={{
          padding: '60px 20px',
          textAlign: 'center',
          color: '#6b7280',
          background: '#f9fafb',
          borderRadius: '8px',
          border: '1px dashed #d1d5db'
        }}>
          <p style={{ margin: 0, fontSize: '16px' }}>
            Select report type and click "Generate Report" to view financial data
          </p>
        </div>
      )}
    </div>
  );
};
// CB:END admin-priority2-financial-reports-viewer-component
